import {
  CardMeta,
  CardMetaGrid,
  ManagementCard,
  ManagementCardList,
  Pill,
} from 'components/ManagementCard'
import { adCodeStrings, commonStrings } from 'constants/strings'
import type { TAdCodeListItem } from 'types'
import { AdCodeActions, AdCodeEditButton } from './AdCodeActions'
import {
  formatCount,
  formatEstimated,
  formatRate,
  mediumLabel,
  statusLabel,
} from './adCodeDisplay'

type Props = {
  adCodes: TAdCodeListItem[]
  loading: boolean
  emptyMessage: string
  onOpen: (adCodeId: number) => void
  /** コピー完了の通知。コピー自体は AdCodeCopyUrlButton が行う。 */
  onCopyUrl: () => void
  onEdit: (adCodeId: number) => void
  onToggleArchive: (adCode: TAdCodeListItem) => void
  archivingId?: number
  className?: string
}

export function AdCodesCardList({
  adCodes,
  loading,
  emptyMessage,
  onOpen,
  onCopyUrl,
  onEdit,
  onToggleArchive,
  archivingId,
  className,
}: Props) {
  return (
    <ManagementCardList
      className={className}
      loading={loading}
      isEmpty={adCodes.length === 0}
      loadingMessage={commonStrings.loading}
      emptyMessage={emptyMessage}
    >
      {adCodes.map((adCode) => {
        const estimated = formatEstimated(adCode.metrics)
        return (
          <ManagementCard
            key={adCode.id}
            title={adCode.name}
            onClick={() => onOpen(adCode.id)}
            ariaLabel={`${adCode.name} の詳細`}
            actions={
              <div className="flex w-full items-start justify-between gap-2">
                <AdCodeEditButton adCodeId={adCode.id} onEdit={onEdit} />
                <AdCodeActions
                  adCode={adCode}
                  onCopyUrl={onCopyUrl}
                  onToggleArchive={onToggleArchive}
                  archivingId={archivingId}
                />
              </div>
            }
            badges={
              <Pill tone={adCode.status === 'ACTIVE' ? 'success' : 'neutral'}>
                {statusLabel(adCode.status)}
              </Pill>
            }
          >
            <p className="break-all text-sm text-ink">
              <span className="text-muted">{adCodeStrings.form.medium}: </span>
              <span>{mediumLabel(adCode.medium)}</span>
            </p>
            <p className="font-mono text-[11px] text-muted">{adCode.code}</p>
            <CardMetaGrid>
              <CardMeta
                label={adCodeStrings.metrics.click}
                value={formatCount(adCode.metrics.clickCount)}
              />
              <CardMeta
                label={adCodeStrings.metrics.registration}
                value={
                  estimated
                    ? `${formatCount(adCode.metrics.confirmedCount)}（${estimated}）`
                    : formatCount(adCode.metrics.confirmedCount)
                }
              />
              <CardMeta
                label={adCodeStrings.metrics.registrationRate}
                value={formatRate(adCode.metrics.registrationRate)}
              />
              <CardMeta
                label={`${adCodeStrings.metrics.blocked24h} / ${adCodeStrings.metrics.blocked}`}
                value={`${formatCount(adCode.metrics.blocked24hCount)} / ${formatCount(adCode.metrics.blockedCount)}`}
              />
            </CardMetaGrid>
          </ManagementCard>
        )
      })}
    </ManagementCardList>
  )
}
